import { useLocation } from 'react-router';
import { ChevronRight } from 'lucide-react';
import { Separator, cn } from '@helios/ui';
import { useAuth } from '@/features/auth/authContext';
import { isRailDestinationActive, railPathFor } from '@/navigation/registry';
import type { NavDomain } from '@/navigation/types';
import { NavItem } from './NavItem';

interface NavGroupSectionProps {
  group: NavDomain;
  /** Sidebar rail (icon-only) mode — no headers, no pins, no disclosure. */
  collapsed?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  onNavigate?: () => void;
  pinnedIds?: readonly string[];
  onTogglePin?: (destinationId: string) => void;
  /** Unique per tree instance so the sidebar and the mobile drawer never collide. */
  idPrefix?: string;
  /** Renders a rule above the section in rail mode. */
  showRailDivider?: boolean;
}

/**
 * One top-level domain, rendered as a disclosure.
 *
 * The header is a real `<button>` carrying `aria-expanded` and `aria-controls`;
 * the panel keeps its `id` in the DOM whether open or closed (hidden via the
 * `hidden` attribute) so the control always points at something real and the
 * hidden rows stay out of both the tab order and the accessibility tree.
 */
export function NavGroupSection({
  group,
  collapsed = false,
  expanded = true,
  onToggle,
  onNavigate,
  pinnedIds,
  onTogglePin,
  idPrefix = 'nav',
  showRailDivider = false,
}: NavGroupSectionProps) {
  const location = useLocation();
  const permissions = useAuth().permissions;
  const panelId = `${idPrefix}-domain-panel-${group.id}`;
  const buttonId = `${idPrefix}-domain-header-${group.id}`;

  if (collapsed) {
    return (
      <li>
        {showRailDivider ? <Separator className="mx-auto my-1 w-8" /> : null}
        <ul aria-label={group.label} className="space-y-0.5">
          {group.items.map((item) => (
            <NavItem
              key={item.id}
              to={railPathFor(item, permissions)}
              label={item.label}
              icon={item.icon}
              collapsed
              end={!item.matchPrefix}
              forceActive={isRailDestinationActive(item.id, location.pathname) || undefined}
            />
          ))}
        </ul>
      </li>
    );
  }

  const Icon = group.icon;

  return (
    <li>
      <button
        type="button"
        id={buttonId}
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={onToggle}
        title={group.summary}
        className={cn(
          'text-2xs focus-visible:ring-ring flex h-7 w-full items-center gap-2 rounded-md border border-transparent px-2 font-semibold uppercase tracking-widest transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset',
          expanded
            ? 'bg-brand-gold/[0.045] text-foreground/85 hover:bg-brand-gold/[0.07] hover:border-white/10'
            : 'text-muted-foreground/80 hover:text-foreground hover:border-white/10 hover:bg-white/[0.05]',
        )}
      >
        <ChevronRight
          className={cn('h-3 w-3 shrink-0 transition-transform', expanded && 'rotate-90')}
          aria-hidden="true"
        />
        <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="truncate">{group.label}</span>
        <span aria-hidden="true" className="text-muted-foreground/50 text-2xs ml-auto font-mono">
          {group.items.length}
        </span>
      </button>

      <ul id={panelId} aria-labelledby={buttonId} hidden={!expanded} className="mt-0.5 space-y-px">
        {group.items.map((item) => (
          <NavItem
            key={item.id}
            to={railPathFor(item, permissions)}
            label={item.label}
            icon={item.icon}
            indented
            onNavigate={onNavigate}
            pinned={pinnedIds?.includes(item.id) ?? false}
            onTogglePin={onTogglePin ? () => onTogglePin(item.id) : undefined}
            end={!item.matchPrefix}
            forceActive={isRailDestinationActive(item.id, location.pathname) || undefined}
          />
        ))}
      </ul>
    </li>
  );
}
